import { useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { message } from 'antd'

import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useStores } from '~/hooks/useStores'
import { useDebounce } from '~/hooks/useDebounce'
import { graphQLErrorMessage } from '~/utils'

import {
  GET_UNREAD_MESSAGE_TOTAL_COUNT,
  READ_MESSAGE,
  READ_ALL_MESSAGE,
} from '~/graphqls/gqls'
import {
  BaseMsgType,
  readMessage,
  readMessageVariables,
  getUnreadTotalCount,
  getUnreadTotalCountVariables,
  readAllMessage,
  readAllMessageVariables,
} from '~/graphqls/types'

export const useMsg = () => {
  const { store } = useStores()

  /** 未读消息总数 */
  const getUnreadTotalCount = useImperativeQuery<
    getUnreadTotalCount,
    getUnreadTotalCountVariables
  >(GET_UNREAD_MESSAGE_TOTAL_COUNT)

  /** 已读单个信息 */
  const [readMsgAction] = useMutation<readMessage, readMessageVariables>(
    READ_MESSAGE
  )
  /** 一键已读所有信息 */
  const [readAllMsgAction] = useMutation<
    readAllMessage,
    readAllMessageVariables
  >(READ_ALL_MESSAGE)

  const handleReadMsg = useCallback(
    (type: BaseMsgType) => {
      return readMsgAction({
        variables: {
          type: type,
        },
      })
    },
    [readMsgAction]
  )
  const handleReadAllMsg = useDebounce(
    async ({ onSuccess }: { onSuccess: () => void }) => {
      try {
        const resp = await readAllMsgAction()
        message.destroy()
        if (resp?.data?.status) {
          message.success('清除未读消息成功')
          onSuccess?.()
        } else {
          message.error('一键清除未读消息失败，请稍后再试~')
        }
      } catch (error) {
        message.destroy()
        message.error(
          graphQLErrorMessage(error) || '一键清除未读消息失败，请稍后再试~'
        )
      }
    },
    [readAllMsgAction]
  )

  /** 未读消息总数 */
  const handleUnreadCount = useCallback(async () => {
    try {
      const resp = await getUnreadTotalCount({
        // 剔除不需要统计的分类
        notNeed: [BaseMsgType.COMMENT],
        // 过滤屏蔽的分类
        skipShield: true,
      })
      if (resp?.data?.count >= 0) {
        store?.setMsgCount(resp?.data?.count)
      }
    } catch (error) {}
  }, [getUnreadTotalCount])

  return {
    readMsgAction,
    /** 已读单个信息 */
    handleReadMsg,
    /** 一键已读所有信息 */
    handleReadAllMsg,
    /** 获取未读消息总数 */
    handleUnreadCount,
  }
}
